import LeadCaptureBox from "@/components/LeadCaptureBox";
import { useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, ChevronRight, Download, Loader2 } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";
import { callAIJson } from "@/lib/ai";

const DOMAIN_AGE_LABELS = ["Brand new", "3 months", "6 months", "1 year", "2 years", "3+ years"] as const;
const CURRENT_MAP = [0, 5, 10, 20, 30, 50, 75, 100, 150, 200, 300, 500] as const;
const TARGET_MAP  = [10, 25, 50, 100, 200, 300, 500, 750, 1000, 1500, 2000, 3000] as const;

type ESP       = "Gmail/GSuite" | "Outlook/O365" | "Custom SMTP";
type EmailType = "Cold outreach" | "Newsletter" | "Transactional";
type DNS       = "All configured" | "Partially configured" | "Not yet";
type Risk      = "low" | "medium" | "high";
type Phase     = "Foundation" | "Growth" | "Cruise";
interface WeekEntry { week: number; vol: number; phase: Phase; }

function safeArr<T>(v: unknown): T[] { return Array.isArray(v) ? (v as T[]) : []; }

function buildSchedule(start: number, target: number, rampPct: number): number[] {
  const weeks: number[] = [];
  let vol = start;
  while (vol < target && weeks.length < 24) { weeks.push(Math.round(vol)); vol = Math.ceil(vol * (1 + rampPct / 100)); }
  weeks.push(target);
  return weeks;
}

function phaseFor(i: number, total: number): Phase {
  const p = i / Math.max(total - 1, 1);
  return p < 0.3 ? "Foundation" : p < 0.65 ? "Growth" : "Cruise";
}

const PHASE_TEXT: Record<Phase, string> = { Foundation: "text-coral", Growth: "text-amber-500", Cruise: "text-teal" };
const PHASE_DOT:  Record<Phase, string> = { Foundation: "bg-coral",   Growth: "bg-amber-400",   Cruise: "bg-teal"  };
const RISK_STYLE: Record<Risk, string>  = { low: "bg-teal/10 border-teal/20 text-teal", medium: "bg-amber-50 border-amber-200 text-amber-600", high: "bg-red-50 border-red-200 text-red-500" };
const RISK_LABEL: Record<Risk, string>  = { low: "Low risk profile", medium: "Medium risk — review carefully", high: "High risk — proceed slowly" };
const RISK_DOT:   Record<Risk, string>  = { low: "bg-teal", medium: "bg-amber-400", high: "bg-red-400" };

function SliderField({ label, id, min, max, value, displayVal, onChange }: { label: string; id: string; min: number; max: number; value: number; displayVal: string; onChange: (v: number) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="text-xs font-semibold text-slate">{label}</label>
        <span className="text-xs font-bold text-coral font-mono">{displayVal}</span>
      </div>
      <input id={id} type="range" min={min} max={max} value={value} step={1} onChange={(e) => onChange(parseInt(e.target.value))} className="w-full h-1.5 rounded-full bg-sand accent-coral cursor-pointer" />
    </div>
  );
}

function RadioGroup<T extends string>({ options, value, onChange }: { options: readonly T[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)}
          className={`px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${value === o ? "bg-coral/10 border-coral/30 text-coral" : "border-black/[0.08] text-slate hover:border-black/[0.14] hover:text-ink"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

export default function WarmupCalculatorPage() {
  const [domainAge,  setDomainAge]  = useState(1);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [targetIdx,  setTargetIdx]  = useState(4);
  const [esp,        setEsp]        = useState<ESP>("Gmail/GSuite");
  const [emailType,  setEmailType]  = useState<EmailType>("Cold outreach");
  const [dns,        setDns]        = useState<DNS>("All configured");
  const [schedule,   setSchedule]   = useState<WeekEntry[] | null>(null);
  const [rampPct,    setRampPct]    = useState(0);
  const [risk,       setRisk]       = useState<Risk>("low");
  const [aiTips,     setAiTips]     = useState<string[]>([]);
  const [loadingAi,  setLoadingAi]  = useState(false);
  const [generated,  setGenerated]  = useState(false);

  const maxVol = schedule ? Math.max(...schedule.map((s) => s.vol)) : 1;

  function generate() {
    const currentVol = CURRENT_MAP[currentIdx];
    const targetVol  = TARGET_MAP[targetIdx];
    let ramp = 40;
    if (domainAge === 0) ramp = 25; else if (domainAge === 1) ramp = 32; else if (domainAge >= 4) ramp = 55;
    if (dns === "Not yet") ramp -= 10; if (dns === "All configured") ramp += 5;
    ramp = Math.max(15, Math.min(70, ramp));
    setRampPct(ramp);
    const startVol = currentVol > 0 ? Math.min(currentVol, Math.max(5, Math.round(targetVol * 0.03))) : Math.max(5, Math.round(targetVol * 0.02));
    const vols = buildSchedule(startVol, targetVol, ramp);
    setSchedule(vols.map((v, i) => ({ week: i + 1, vol: v, phase: phaseFor(i, vols.length) })));
    setRisk(domainAge === 0 || dns === "Not yet" ? "high" : domainAge <= 1 || dns === "Partially configured" ? "medium" : "low");
    setGenerated(true);
    void fetchAiTips(DOMAIN_AGE_LABELS[domainAge], currentVol, targetVol, esp, emailType, dns, vols.length, vols[0], ramp);
  }

  async function fetchAiTips(ageLabel: string, curVol: number, tgtVol: number, espVal: string, typeVal: string, dnsVal: string, weeks: number, startVol: number, ramp: number) {
    setLoadingAi(true); setAiTips([]);
    const prompt = `You are an expert email deliverability consultant. Give 4 specific actionable warmup tips for:\n- Domain age: ${ageLabel}, Current: ${curVol}/day, Target: ${tgtVol}/day\n- ESP: ${espVal}, Type: ${typeVal}, DNS: ${dnsVal}\n- Plan: ${weeks} weeks, starting ${startVol}/day, ramp +${ramp}%/week\nReturn ONLY valid JSON: {"tips":["<tip1>","<tip2>","<tip3>","<tip4>"]}`;
    try {
      const p = await callAIJson<{ tips?: unknown }>({ prompt, maxTokens: 600 });
      setAiTips(safeArr<string>(p.tips).slice(0, 4));
    } catch {
      setAiTips(["Could not load AI tips. Your schedule above is still accurate."]);
    } finally { setLoadingAi(false); }
  }

  function exportCSV() {
    if (!schedule) return;
    const rows = [["Week", "Daily Limit", "Phase"], ...schedule.map((r) => [r.week, r.vol, r.phase])];
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(rows.map((r) => r.join(",")).join("\n"));
    a.download = "moremail-warmup-schedule.csv";
    a.click();
  }

  return (
    <>
      <SeoHead title="Free Email Warmup Calculator — MoreMail | Custom Warmup Schedule" description="Generate a custom email warmup schedule based on your domain age, target volume, and ESP. Free, instant, downloadable CSV." url={`${SITE_URL}/warmup-calculator`} breadcrumbs={[{ name: "Home", url: SITE_URL }, { name: "Warmup Calculator", url: `${SITE_URL}/warmup-calculator` }]} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate">
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-ink">Warmup Calculator</span>
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 sm:pb-8 text-center">
        <div className="inline-flex items-center px-3 py-1.5 bg-coral/10 border border-coral/20 rounded-full text-coral text-xs font-bold mb-4">Free Tool — No signup required</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-3">Your personal email{" "}<em className="text-coral not-italic">warmup plan</em></h1>
        <p className="text-slate text-sm sm:text-base max-w-xl mx-auto leading-relaxed">Tell us your setup. We'll generate a week-by-week warmup schedule so your emails land in the inbox — not spam.</p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] xl:grid-cols-[320px_1fr] gap-5 lg:gap-6 items-start">

          {/* Controls */}
          <div className="bg-white rounded-2xl border border-black/[0.08] p-5 sm:p-6 shadow-sm lg:sticky lg:top-[70px] lg:max-h-[calc(100vh-90px)] lg:overflow-y-auto">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate mb-4 sm:mb-5">Your setup</h2>
            <div className="space-y-4 sm:space-y-5">
              <SliderField label="Domain age" id="domainAge" min={0} max={5} value={domainAge} displayVal={DOMAIN_AGE_LABELS[domainAge]} onChange={setDomainAge} />
              <SliderField label="Current daily sends" id="currentSends" min={0} max={11} value={currentIdx} displayVal={`${CURRENT_MAP[currentIdx].toLocaleString()} / day`} onChange={setCurrentIdx} />
              <SliderField label="Target daily sends" id="targetSends" min={0} max={11} value={targetIdx} displayVal={`${TARGET_MAP[targetIdx].toLocaleString()} / day`} onChange={setTargetIdx} />
              <div><p className="text-xs font-semibold text-slate mb-2">Email provider</p><RadioGroup<ESP> options={["Gmail/GSuite", "Outlook/O365", "Custom SMTP"] as const} value={esp} onChange={setEsp} /></div>
              <div><p className="text-xs font-semibold text-slate mb-2">Email type</p><RadioGroup<EmailType> options={["Cold outreach", "Newsletter", "Transactional"] as const} value={emailType} onChange={setEmailType} /></div>
              <div><p className="text-xs font-semibold text-slate mb-2">SPF / DKIM / DMARC</p><RadioGroup<DNS> options={["All configured", "Partially configured", "Not yet"] as const} value={dns} onChange={setDns} /></div>
            </div>
            <button type="button" onClick={generate} className="mt-5 sm:mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-coral text-white text-sm font-semibold rounded-full hover:bg-coral-dark transition-colors shadow-md shadow-coral/20">
              Generate warmup plan <ArrowRight size={14} />
            </button>
          </div>

          {/* Results */}
          <div>
            {!generated ? (
              <div className="bg-white rounded-2xl border border-black/[0.08] p-10 sm:p-12 text-center">
                <p className="text-3xl sm:text-4xl mb-4 opacity-30" aria-hidden="true">📬</p>
                <p className="text-sm text-slate">Adjust your settings and click <strong className="text-ink">Generate warmup plan</strong> to see your custom schedule.</p>
              </div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                  {[{ label: "Duration", val: String(schedule!.length), sub: "weeks" }, { label: "Day 1 limit", val: schedule![0].vol.toLocaleString(), sub: "emails/day" }, { label: "Weekly ramp", val: `+${rampPct}%`, sub: "increase" }].map(({ label, val, sub }) => (
                    <div key={label} className="bg-white rounded-2xl border border-black/[0.06] py-3 sm:py-4 text-center px-2">
                      <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-slate mb-1">{label}</p>
                      <p className="font-display text-lg sm:text-2xl font-bold text-coral leading-none truncate">{val}</p>
                      <p className="text-[10px] sm:text-xs text-slate mt-1">{sub}</p>
                    </div>
                  ))}
                </div>

                {/* Risk badge */}
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold mb-4 ${RISK_STYLE[risk]}`}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${RISK_DOT[risk]}`} />{RISK_LABEL[risk]}
                </div>

                {/* Schedule table */}
                <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden mb-4">
                  <div className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] bg-paper flex items-center justify-between gap-3">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate">Week-by-week schedule</p>
                    <button type="button" onClick={exportCSV} className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg border border-black/[0.08] text-slate text-xs hover:bg-sand transition-colors whitespace-nowrap">
                      <Download size={11} />Export CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[380px]">
                      <thead>
                        <tr className="border-b border-black/[0.04]">
                          {["Week", "Daily limit", "Phase", "Volume"].map((h) => (
                            <th key={h} className="text-left px-3 sm:px-5 py-2.5 text-[10px] font-bold uppercase tracking-wide text-slate/60 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-black/[0.04]">
                        {schedule!.map(({ week, vol, phase }) => {
                          const pct = Math.round((vol / maxVol) * 100);
                          return (
                            <tr key={week} className="hover:bg-paper/50 transition-colors">
                              <td className="px-3 sm:px-5 py-3 font-display font-bold text-ink whitespace-nowrap">Week {week}</td>
                              <td className="px-3 sm:px-5 py-3 text-slate whitespace-nowrap">{vol.toLocaleString()}</td>
                              <td className="px-3 sm:px-5 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${PHASE_TEXT[phase]}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PHASE_DOT[phase]}`} />{phase}
                                </span>
                              </td>
                              <td className="px-3 sm:px-5 py-3">
                                <div className="flex items-center gap-2 min-w-[80px]">
                                  <div className="flex-1 h-1.5 rounded-full bg-sand overflow-hidden"><div className="h-full bg-coral rounded-full" style={{ width: `${pct}%` }} /></div>
                                  <span className="text-xs font-mono text-slate w-7 text-right flex-shrink-0">{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* AI tips */}
                <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-teal flex-shrink-0" />
                    <p className="text-xs font-bold uppercase tracking-widest text-slate">AI recommendations for your setup</p>
                  </div>
                  {loadingAi ? (
                    <div className="flex items-center gap-2 text-slate text-sm py-2"><Loader2 size={14} className="animate-spin" />Generating personalized tips...</div>
                  ) : (
                    <div className="space-y-2">
                      {aiTips.map((tip, i) => (
                        <div key={i} className="flex gap-3 bg-paper rounded-xl px-3 sm:px-4 py-3 text-sm border-l-2 border-coral">
                          <span className="font-bold text-coral flex-shrink-0">{i + 1}.</span>
                          <span className="text-slate leading-relaxed">{tip}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="bg-ink rounded-2xl px-5 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-1">Let MoreMail run your warmup automatically</h3>
                    <p className="text-sm text-white/50">Real AI warmup — we handle sending, monitoring, and adjusting.</p>
                  </div>
                  <LeadCaptureBox />
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
